import { formatDateSlashes, newDate } from '~/services/TimeService';

import type {
  CharterPricingRow,
  FareFamilyPricingPreviewItem,
  FareFamilyPricingPreviewResult,
  FareFamilyPricingResult,
  FareFamilyPricingUpdatePayload,
  ParseFailure,
  ParseResult,
} from './CharterFlightsPricingTypes';

export const DEPARTING_DEP_AIRPORT = 'MEL';
export const DEPARTING_ARR_AIRPORT = 'MLE';
export const RETURNING_DEP_AIRPORT = 'MLE';
export const RETURNING_ARR_AIRPORT = 'MEL';

export const DATE_FORMAT = 'DD/MM/YYYY';

type LuxPlusPayloadField = keyof NonNullable<FareFamilyPricingUpdatePayload['luxPlus']>;

const LUX_PLUS_FIELD_TO_PAYLOAD_FIELD: Partial<Record<keyof CharterPricingRow, LuxPlusPayloadField>> = {
  luxPlusPackagedAdultEconomyPrice: 'bundledReturnAdultEconomyPrice',
  luxPlusPackagedAdultBusinessPrice: 'bundledReturnAdultBusinessPrice',
  luxPlusPackagedAdultPremiumEconomyUpsell: 'bundledReturnAdultPremiumEconomyUpsell',
  luxPlusPackagedAdultSkyLoungeUpsell: 'bundledReturnAdultSkyloungeUpsell',
  luxPlusPackagedAdultLuxRowPaxOneUpsell: 'bundledReturnAdultLuxRowPaxOneUpsell',
  luxPlusPackagedChildEconomyMultiplier: 'bundledReturnChildEconomyMultiplier',
  luxPlusPackagedChildBusinessMultiplier: 'bundledReturnChildBusinessMultiplier',
  luxPlusPackagedInfantEconomyMultiplier: 'bundledReturnInfantEconomyMultiplier',
  luxPlusPackagedInfantBusinessMultiplier: 'bundledReturnInfantBusinessMultiplier',
  luxPlusStandaloneReturnBusinessMarkup: 'standaloneReturnBusinessMultiplier',
  luxPlusStandaloneReturnEconomyMarkup: 'standaloneReturnEconomyMultiplier',
  luxPlusStandaloneOneWayBusinessMarkup: 'standaloneOneWayBusinessMultiplier',
  luxPlusStandaloneOneWayEconomyMarkup: 'standaloneOneWayEconomyMultiplier',
  luxPlusPackagedAdultEconomyFree: 'packagedAdultEconomyFree',
  luxPlusPackagedChildEconomyFree: 'packagedChildEconomyFree',
};

/**
 * Converts API multiplier to markup decimal with rounding to avoid floating-point precision issues.
 * Example: 1.1 (multiplier) -> 0.1 (10% markup)
 */
function multiplierToMarkup(multiplier: number): number {
  // Round to 4 decimal places to avoid precision issues like 1.1 - 1 = 0.10000000000000009
  return Math.round((multiplier - 1) * 10000) / 10000;
}

export function mapApiResultToRow(item: FareFamilyPricingResult): CharterPricingRow {
  const formattedDate = formatDateSlashes(item.depDate);
  const luxPlus = item.luxPlus;
  const hasLuxPlusField = (field: string) => (luxPlus ? (luxPlus.configuredFields?.includes(field) ?? true) : false);

  return {
    id: item.flightInstanceId,
    flightInstanceId: item.flightInstanceId,
    offeredForSale: item.offeredForSale,
    pricingType: 'Standard',
    direction: `${item.depAirport}-${item.arrAirport}`,
    departureDate: formattedDate,
    packagedAdultBusinessPrice: item.bundledReturnAdultBusinessPrice,
    packagedAdultEconomyPrice: item.bundledReturnAdultEconomyPrice,
    packagedAdultEconomyFree: item.packagedAdultEconomyFree,
    packagedAdultSkyLoungeUpsell: item.bundledReturnAdultSkyloungeUpsell,
    packagedAdultPremiumEconomyUpsell: item.bundledReturnAdultPremiumEconomyUpsell,
    packagedAdultLuxRowPaxOneUpsell: item.bundledReturnAdultLuxRowPaxOneUpsell,
    packagedChildBusinessMultiplier: item.bundledReturnChildBusinessMultiplier,
    packagedChildEconomyMultiplier: item.bundledReturnChildEconomyMultiplier,
    packagedChildEconomyFree: item.packagedChildEconomyFree,
    packagedInfantBusinessMultiplier: item.bundledReturnInfantBusinessMultiplier,
    packagedInfantEconomyMultiplier: item.bundledReturnInfantEconomyMultiplier,
    // Convert API multipliers to UI markups: 1.1 -> 0.1 (10%)
    standaloneReturnBusinessMarkup: multiplierToMarkup(item.standaloneReturnBusinessMultiplier),
    standaloneReturnEconomyMarkup: multiplierToMarkup(item.standaloneReturnEconomyMultiplier),
    standaloneOneWayBusinessMarkup: multiplierToMarkup(item.standaloneOneWayBusinessMultiplier),
    standaloneOneWayEconomyMarkup: multiplierToMarkup(item.standaloneOneWayEconomyMultiplier),
    luxPlusPackagedAdultBusinessPrice: hasLuxPlusField('bundledReturnAdultBusinessPrice')
      ? (luxPlus?.bundledReturnAdultBusinessPrice ?? null)
      : null,
    luxPlusPackagedAdultEconomyPrice: hasLuxPlusField('bundledReturnAdultEconomyPrice')
      ? (luxPlus?.bundledReturnAdultEconomyPrice ?? null)
      : null,
    luxPlusPackagedAdultEconomyFree: hasLuxPlusField('packagedAdultEconomyFree')
      ? (luxPlus?.packagedAdultEconomyFree ?? null)
      : null,
    luxPlusPackagedAdultSkyLoungeUpsell: hasLuxPlusField('bundledReturnAdultSkyloungeUpsell')
      ? (luxPlus?.bundledReturnAdultSkyloungeUpsell ?? null)
      : null,
    luxPlusPackagedAdultPremiumEconomyUpsell: hasLuxPlusField('bundledReturnAdultPremiumEconomyUpsell')
      ? (luxPlus?.bundledReturnAdultPremiumEconomyUpsell ?? null)
      : null,
    luxPlusPackagedAdultLuxRowPaxOneUpsell: hasLuxPlusField('bundledReturnAdultLuxRowPaxOneUpsell')
      ? (luxPlus?.bundledReturnAdultLuxRowPaxOneUpsell ?? null)
      : null,
    luxPlusPackagedChildBusinessMultiplier: hasLuxPlusField('bundledReturnChildBusinessMultiplier')
      ? (luxPlus?.bundledReturnChildBusinessMultiplier ?? null)
      : null,
    luxPlusPackagedChildEconomyMultiplier: hasLuxPlusField('bundledReturnChildEconomyMultiplier')
      ? (luxPlus?.bundledReturnChildEconomyMultiplier ?? null)
      : null,
    luxPlusPackagedChildEconomyFree: hasLuxPlusField('packagedChildEconomyFree')
      ? (luxPlus?.packagedChildEconomyFree ?? null)
      : null,
    luxPlusPackagedInfantBusinessMultiplier: hasLuxPlusField('bundledReturnInfantBusinessMultiplier')
      ? (luxPlus?.bundledReturnInfantBusinessMultiplier ?? null)
      : null,
    luxPlusPackagedInfantEconomyMultiplier: hasLuxPlusField('bundledReturnInfantEconomyMultiplier')
      ? (luxPlus?.bundledReturnInfantEconomyMultiplier ?? null)
      : null,
    luxPlusStandaloneReturnBusinessMarkup:
      luxPlus && hasLuxPlusField('standaloneReturnBusinessMultiplier')
        ? multiplierToMarkup(luxPlus.standaloneReturnBusinessMultiplier)
        : null,
    luxPlusStandaloneReturnEconomyMarkup:
      luxPlus && hasLuxPlusField('standaloneReturnEconomyMultiplier')
        ? multiplierToMarkup(luxPlus.standaloneReturnEconomyMultiplier)
        : null,
    luxPlusStandaloneOneWayBusinessMarkup:
      luxPlus && hasLuxPlusField('standaloneOneWayBusinessMultiplier')
        ? multiplierToMarkup(luxPlus.standaloneOneWayBusinessMultiplier)
        : null,
    luxPlusStandaloneOneWayEconomyMarkup:
      luxPlus && hasLuxPlusField('standaloneOneWayEconomyMultiplier')
        ? multiplierToMarkup(luxPlus.standaloneOneWayEconomyMultiplier)
        : null,
  };
}

function buildPreviewItemFromRow(row: CharterPricingRow): FareFamilyPricingPreviewItem {
  return {
    depDate: row.departureDate,
    direction: row.direction,
    bundledReturnAdultEconomyPrice: row.packagedAdultEconomyPrice,
    bundledReturnAdultBusinessPrice: row.packagedAdultBusinessPrice,
    bundledReturnAdultPremiumEconomyUpsell: row.packagedAdultPremiumEconomyUpsell,
    bundledReturnAdultSkyloungeUpsell: row.packagedAdultSkyLoungeUpsell,
    bundledReturnAdultLuxRowPaxOneUpsell: row.packagedAdultLuxRowPaxOneUpsell,
    bundledReturnChildEconomyMultiplier: row.packagedChildEconomyMultiplier,
    bundledReturnChildBusinessMultiplier: row.packagedChildBusinessMultiplier,
    bundledReturnInfantEconomyMultiplier: row.packagedInfantEconomyMultiplier,
    bundledReturnInfantBusinessMultiplier: row.packagedInfantBusinessMultiplier,
    standaloneReturnEconomyMultiplier: markupToMultiplier(row.standaloneReturnEconomyMarkup),
    standaloneReturnBusinessMultiplier: markupToMultiplier(row.standaloneReturnBusinessMarkup),
    standaloneOneWayEconomyMultiplier: markupToMultiplier(row.standaloneOneWayEconomyMarkup),
    standaloneOneWayBusinessMultiplier: markupToMultiplier(row.standaloneOneWayBusinessMarkup),
    packagedAdultEconomyFree: row.packagedAdultEconomyFree,
    packagedChildEconomyFree: row.packagedChildEconomyFree,
  };
}

/**
 * Builds the effective LuxPlus inputs for preview/export. Nullable LuxPlus
 * price and multiplier overrides inherit the standard input; the independent
 * LuxPlus free flags are off unless explicitly enabled.
 */
function buildLuxPlusPreviewItemFromRow(row: CharterPricingRow): FareFamilyPricingPreviewItem {
  return {
    depDate: row.departureDate,
    direction: row.direction,
    bundledReturnAdultEconomyPrice: row.luxPlusPackagedAdultEconomyPrice ?? row.packagedAdultEconomyPrice,
    bundledReturnAdultBusinessPrice: row.luxPlusPackagedAdultBusinessPrice ?? row.packagedAdultBusinessPrice,
    bundledReturnAdultPremiumEconomyUpsell:
      row.luxPlusPackagedAdultPremiumEconomyUpsell ?? row.packagedAdultPremiumEconomyUpsell,
    bundledReturnAdultSkyloungeUpsell: row.luxPlusPackagedAdultSkyLoungeUpsell ?? row.packagedAdultSkyLoungeUpsell,
    bundledReturnAdultLuxRowPaxOneUpsell:
      row.luxPlusPackagedAdultLuxRowPaxOneUpsell ?? row.packagedAdultLuxRowPaxOneUpsell,
    bundledReturnChildEconomyMultiplier:
      row.luxPlusPackagedChildEconomyMultiplier ?? row.packagedChildEconomyMultiplier,
    bundledReturnChildBusinessMultiplier:
      row.luxPlusPackagedChildBusinessMultiplier ?? row.packagedChildBusinessMultiplier,
    bundledReturnInfantEconomyMultiplier:
      row.luxPlusPackagedInfantEconomyMultiplier ?? row.packagedInfantEconomyMultiplier,
    bundledReturnInfantBusinessMultiplier:
      row.luxPlusPackagedInfantBusinessMultiplier ?? row.packagedInfantBusinessMultiplier,
    standaloneReturnEconomyMultiplier: markupToMultiplier(
      row.luxPlusStandaloneReturnEconomyMarkup ?? row.standaloneReturnEconomyMarkup,
    ),
    standaloneReturnBusinessMultiplier: markupToMultiplier(
      row.luxPlusStandaloneReturnBusinessMarkup ?? row.standaloneReturnBusinessMarkup,
    ),
    standaloneOneWayEconomyMultiplier: markupToMultiplier(
      row.luxPlusStandaloneOneWayEconomyMarkup ?? row.standaloneOneWayEconomyMarkup,
    ),
    standaloneOneWayBusinessMultiplier: markupToMultiplier(
      row.luxPlusStandaloneOneWayBusinessMarkup ?? row.standaloneOneWayBusinessMarkup,
    ),
    packagedAdultEconomyFree: Boolean(row.luxPlusPackagedAdultEconomyFree),
    packagedChildEconomyFree: Boolean(row.luxPlusPackagedChildEconomyFree),
  };
}

export function formatPercent(value: number | null | undefined): string {
  const safeValue = value == null || Number.isNaN(value) ? 0 : value;
  return `${(safeValue * 100).toFixed(1)}%`;
}

/**
 * Converts internal decimal value to display value for editing.
 * Example: 0.1 (internal) -> 10 (displayed in edit input)
 * Uses rounding to avoid floating-point precision issues (e.g., 1.1 - 1 = 0.10000000000000009)
 */
export function percentToDisplay(value: number | null | undefined): number {
  const safeValue = value == null || Number.isNaN(value) ? 0 : value;
  // Round to 2 decimal places to fix floating-point precision issues
  return Math.round(safeValue * 100 * 100) / 100;
}

/**
 * Converts display value back to internal decimal for storage.
 * Example: 10 (user input) -> 0.1 (stored internally)
 * Uses rounding to avoid floating-point precision issues
 */
export function displayToPercent(value: number | null | undefined): number {
  const safeValue = value == null || Number.isNaN(value) ? 0 : value;
  // Round to 4 decimal places to fix floating-point precision issues
  return Math.round((safeValue / 100) * 10000) / 10000;
}

/**
 * Formats a whole number percentage value with % suffix.
 * Used after valueGetter has already converted decimal to whole number.
 * Example: 10 -> "10.0%"
 */
export function formatPercentDisplay(value: number | null | undefined): string {
  const safeValue = value == null || Number.isNaN(value) ? 0 : value;
  return `${safeValue.toFixed(1)}%`;
}

export function formatCurrency(value: number | null | undefined): string {
  const safeValue = value == null || Number.isNaN(value) ? 0 : value;
  return `$${safeValue.toLocaleString()}`;
}

export function isParseFailure(result: ParseResult): result is ParseFailure {
  return !result.success;
}

export function parseCurrency(value: string | number | null | undefined): ParseResult {
  if (value == null || value === '') {
    return { success: true, value: 0 };
  }
  if (typeof value === 'number') {
    if (Number.isNaN(value)) {
      return { success: false, error: 'Invalid number' };
    }
    return { success: true, value };
  }
  const cleaned = value.replace(/[$,]/g, '').trim();
  if (cleaned === '') {
    return { success: true, value: 0 };
  }
  const parsed = parseFloat(cleaned);
  if (Number.isNaN(parsed)) {
    return { success: false, error: `"${value}" is not a valid currency value` };
  }
  return { success: true, value: parsed };
}

export function parsePercent(value: string | number | null | undefined): ParseResult {
  if (value == null || value === '') {
    return { success: true, value: 0 };
  }
  if (typeof value === 'number') {
    if (Number.isNaN(value)) {
      return { success: false, error: 'Invalid number' };
    }
    return { success: true, value };
  }
  const cleaned = value.replace(/%/g, '').trim();
  if (cleaned === '') {
    return { success: true, value: 0 };
  }
  const parsed = parseFloat(cleaned);
  if (Number.isNaN(parsed)) {
    return { success: false, error: `"${value}" is not a valid percentage` };
  }

  return { success: true, value: parsed };
}

type NumericField = {
  [K in keyof CharterPricingRow]: CharterPricingRow[K] extends number ? K : never;
}[keyof CharterPricingRow];

const CURRENCY_FIELDS: Array<NumericField> = [
  'packagedAdultBusinessPrice',
  'packagedAdultEconomyPrice',
  'packagedAdultSkyLoungeUpsell',
  'packagedAdultPremiumEconomyUpsell',
  'packagedAdultLuxRowPaxOneUpsell',
];

const PERCENT_FIELDS: Array<NumericField> = [
  'packagedChildBusinessMultiplier',
  'packagedChildEconomyMultiplier',
  'packagedInfantBusinessMultiplier',
  'packagedInfantEconomyMultiplier',
  'standaloneReturnBusinessMarkup',
  'standaloneReturnEconomyMarkup',
  'standaloneOneWayBusinessMarkup',
  'standaloneOneWayEconomyMarkup',
];

export function parseAndValidateRow(row: CharterPricingRow): CharterPricingRow {
  const validated = { ...row };

  for (const field of CURRENCY_FIELDS) {
    const rawValue = validated[field];
    const result = parseCurrency(rawValue);
    if (isParseFailure(result)) {
      throw new Error(`${field}: ${result.error}`);
    }
    validated[field] = result.value;
  }

  for (const field of PERCENT_FIELDS) {
    const rawValue = validated[field];
    const result = parsePercent(rawValue);
    if (isParseFailure(result)) {
      throw new Error(`${field}: ${result.error}`);
    }
    validated[field] = result.value;
  }

  return validated;
}

/**
 * Converts markup decimal to API multiplier with rounding to avoid floating-point precision issues.
 * Example: 0.1 (10% markup) -> 1.1 (multiplier)
 */
function markupToMultiplier(markup: number): number {
  // Round to 4 decimal places to avoid precision issues like 0.1 + 1 = 1.1000000000000001
  return Math.round((markup + 1) * 10000) / 10000;
}

export function buildUpdatePayload(
  editedRows: Map<string, CharterPricingRow>,
  luxPlusTouchedFields?: Map<string, Set<keyof CharterPricingRow>>,
): FareFamilyPricingUpdatePayload {
  const rows = Array.from(editedRows.values());

  return {
    flightInstanceIds: rows.map((r) => r.flightInstanceId),
    bundledReturnAdultEconomyPrice: rows.map((r) => r.packagedAdultEconomyPrice),
    bundledReturnAdultBusinessPrice: rows.map((r) => r.packagedAdultBusinessPrice),
    bundledReturnAdultPremiumEconomyUpsell: rows.map((r) => r.packagedAdultPremiumEconomyUpsell),
    bundledReturnAdultSkyloungeUpsell: rows.map((r) => r.packagedAdultSkyLoungeUpsell),
    bundledReturnAdultLuxRowPaxOneUpsell: rows.map((r) => r.packagedAdultLuxRowPaxOneUpsell),
    bundledReturnChildEconomyMultiplier: rows.map((r) => r.packagedChildEconomyMultiplier),
    bundledReturnChildBusinessMultiplier: rows.map((r) => r.packagedChildBusinessMultiplier),
    bundledReturnInfantEconomyMultiplier: rows.map((r) => r.packagedInfantEconomyMultiplier),
    bundledReturnInfantBusinessMultiplier: rows.map((r) => r.packagedInfantBusinessMultiplier),
    // Convert UI markups back to API multipliers: 0.1 (10%) -> 1.1
    standaloneReturnBusinessMultiplier: rows.map((r) => markupToMultiplier(r.standaloneReturnBusinessMarkup)),
    standaloneReturnEconomyMultiplier: rows.map((r) => markupToMultiplier(r.standaloneReturnEconomyMarkup)),
    standaloneOneWayBusinessMultiplier: rows.map((r) => markupToMultiplier(r.standaloneOneWayBusinessMarkup)),
    standaloneOneWayEconomyMultiplier: rows.map((r) => markupToMultiplier(r.standaloneOneWayEconomyMarkup)),
    packagedAdultEconomyFree: rows.map((r) => r.packagedAdultEconomyFree),
    packagedChildEconomyFree: rows.map((r) => r.packagedChildEconomyFree),
    offeredForSale: rows.map((r) => r.offeredForSale),
    luxPlus: {
      bundledReturnAdultEconomyPrice: rows.map((r) => r.luxPlusPackagedAdultEconomyPrice),
      bundledReturnAdultBusinessPrice: rows.map((r) => r.luxPlusPackagedAdultBusinessPrice),
      bundledReturnAdultPremiumEconomyUpsell: rows.map((r) => r.luxPlusPackagedAdultPremiumEconomyUpsell),
      bundledReturnAdultSkyloungeUpsell: rows.map((r) => r.luxPlusPackagedAdultSkyLoungeUpsell),
      bundledReturnAdultLuxRowPaxOneUpsell: rows.map((r) => r.luxPlusPackagedAdultLuxRowPaxOneUpsell),
      bundledReturnChildEconomyMultiplier: rows.map((r) => r.luxPlusPackagedChildEconomyMultiplier),
      bundledReturnChildBusinessMultiplier: rows.map((r) => r.luxPlusPackagedChildBusinessMultiplier),
      bundledReturnInfantEconomyMultiplier: rows.map((r) => r.luxPlusPackagedInfantEconomyMultiplier),
      bundledReturnInfantBusinessMultiplier: rows.map((r) => r.luxPlusPackagedInfantBusinessMultiplier),
      standaloneReturnBusinessMultiplier: rows.map((r) =>
        r.luxPlusStandaloneReturnBusinessMarkup == null
          ? null
          : markupToMultiplier(r.luxPlusStandaloneReturnBusinessMarkup),
      ),
      standaloneReturnEconomyMultiplier: rows.map((r) =>
        r.luxPlusStandaloneReturnEconomyMarkup == null
          ? null
          : markupToMultiplier(r.luxPlusStandaloneReturnEconomyMarkup),
      ),
      standaloneOneWayBusinessMultiplier: rows.map((r) =>
        r.luxPlusStandaloneOneWayBusinessMarkup == null
          ? null
          : markupToMultiplier(r.luxPlusStandaloneOneWayBusinessMarkup),
      ),
      standaloneOneWayEconomyMultiplier: rows.map((r) =>
        r.luxPlusStandaloneOneWayEconomyMarkup == null
          ? null
          : markupToMultiplier(r.luxPlusStandaloneOneWayEconomyMarkup),
      ),
      packagedAdultEconomyFree: rows.map((r) => r.luxPlusPackagedAdultEconomyFree),
      packagedChildEconomyFree: rows.map((r) => r.luxPlusPackagedChildEconomyFree),
    },
    ...(luxPlusTouchedFields && {
      luxPlusTouchedFields: rows.map((row) =>
        [...(luxPlusTouchedFields.get(row.flightInstanceId) ?? new Set())]
          .map((field) => LUX_PLUS_FIELD_TO_PAYLOAD_FIELD[field])
          .filter((field): field is LuxPlusPayloadField => field !== undefined),
      ),
    }),
  };
}

// Builds preview payload with per-leg items (backend handles roundtrip combination)
export function buildPreviewPayloadFromRows(allRows: Array<CharterPricingRow>): {
  items: Array<FareFamilyPricingPreviewItem>;
} {
  return { items: allRows.map(buildPreviewItemFromRow) };
}

export function buildLuxPlusPreviewPayloadFromRows(allRows: Array<CharterPricingRow>): {
  items: Array<FareFamilyPricingPreviewItem>;
} {
  return { items: allRows.map(buildLuxPlusPreviewItemFromRow) };
}

// Builds preview payload from raw API results
export function buildPreviewPayload(apiResults: Array<FareFamilyPricingResult>): {
  items: Array<FareFamilyPricingPreviewItem>;
} {
  const allRows = apiResults.map(mapApiResultToRow);
  return buildPreviewPayloadFromRows(allRows);
}

// Fare family columns in order for CSV export
const FARE_FAMILY_LABELS = ['Economy', 'Economy Extra', 'Premium Economy', 'Economy Extra for One', 'Business'];

const CSV_GROUPS = [
  { title: 'Adult Roundtrip 7D prices - Packaged Fares', priceKey: 'computedBundledReturnAdult' },
  { title: 'Child Roundtrip 7D prices - Packaged Fares', priceKey: 'computedBundledReturnChild' },
  { title: 'Infant Roundtrip 7D prices - Packaged Fares', priceKey: 'computedBundledReturnInfant' },
  { title: 'Adult Roundtrip 7D prices - Standalone RoundTrip Fares', priceKey: 'computedStandaloneReturnAdult' },
  { title: 'Child Roundtrip 7D prices - Standalone RoundTrip Fares', priceKey: 'computedStandaloneReturnChild' },
  { title: 'Infant Roundtrip 7D prices - Standalone RoundTrip Fares', priceKey: 'computedStandaloneReturnInfant' },
  { title: 'Adult One-way MEL-MLE prices - Standalone One-way Fares', priceKey: 'computedStandaloneOneWayAdult' },
  { title: 'Child One-way MEL-MLE prices - Standalone One-way Fares', priceKey: 'computedStandaloneOneWayChild' },
  { title: 'Infant One-way MEL-MLE prices - Standalone One-way Fares', priceKey: 'computedStandaloneOneWayInfant' },
] as const;

const FARE_FAMILY_KEYS = ['economy', 'skylounge', 'premium_economy', 'lux_row_pax_one', 'business'] as const;

export function generateCsvDataFromPreview(
  previewResults: Array<FareFamilyPricingPreviewResult>,
  luxPlusPreviewResults: Array<FareFamilyPricingPreviewResult> = [],
): Array<Array<string | number>> {
  const csvRows: Array<Array<string | number>> = [];

  // Build header row 1: Group titles with blank separators
  const titleRow: Array<string | number> = ['', ''];
  for (let i = 0; i < CSV_GROUPS.length; i++) {
    titleRow.push(CSV_GROUPS[i].title);
    // Add empty cells for remaining fare family columns (4 more per group)
    for (let j = 0; j < FARE_FAMILY_LABELS.length - 1; j++) {
      titleRow.push('');
    }
    // Add blank separator column between groups (except after last group)
    if (i < CSV_GROUPS.length - 1) {
      titleRow.push('');
    }
  }
  csvRows.push(titleRow);

  // Build header row 2: Empty row
  const emptyRow: Array<string | number> = Array(titleRow.length).fill('');
  csvRows.push(emptyRow);

  // Build header row 3: Fare family labels for each group
  const labelRow: Array<string | number> = ['Departure Date', 'Pricing'];
  for (let i = 0; i < CSV_GROUPS.length; i++) {
    labelRow.push(...FARE_FAMILY_LABELS);
    // Add blank separator column between groups (except after last group)
    if (i < CSV_GROUPS.length - 1) {
      labelRow.push('');
    }
  }
  csvRows.push(labelRow);

  // Build header row 4: Empty row
  csvRows.push([...emptyRow]);

  const sortedResults = [...previewResults].sort((a, b) => {
    const dateA = newDate(a.depDate, DATE_FORMAT);
    const dateB = newDate(b.depDate, DATE_FORMAT);
    return dateA.valueOf() - dateB.valueOf();
  });

  const luxPlusResultsByDate = new Map(luxPlusPreviewResults.map((result) => [result.depDate, result]));

  const appendDataRow = (result: FareFamilyPricingPreviewResult, pricing: 'Standard' | 'LuxPlus') => {
    const { computedPrices } = result;
    const dataRow: Array<string | number> = [result.depDate, pricing];

    for (let i = 0; i < CSV_GROUPS.length; i++) {
      const priceKey = CSV_GROUPS[i].priceKey;

      for (const fareKey of FARE_FAMILY_KEYS) {
        const fareFamily = computedPrices[fareKey];
        dataRow.push(fareFamily[priceKey]);
      }
      // Add blank separator column between groups (except after last group)
      if (i < CSV_GROUPS.length - 1) {
        dataRow.push('');
      }
    }
    csvRows.push(dataRow);
  };

  for (const result of sortedResults) {
    appendDataRow(result, 'Standard');

    const luxPlusResult = luxPlusResultsByDate.get(result.depDate);
    if (luxPlusResult) {
      appendDataRow(luxPlusResult, 'LuxPlus');
    }
  }

  return csvRows;
}
